import { useEffect, useState } from "react";
import { MEDIA_MOBILE } from "./breakpoints";
import { useReducedMotion, useScroll, useTransform, type MotionStyle } from "framer-motion";

type Axis = "x" | "y";
export type TimelineStage = "cta" | "social" | "spotify" | "contact";
export type RevealRange = [number, number, number, number];
export type ThreePointRange = [number, number, number];
export type StageWindow = {
  reveal: RevealRange;
  active: [number, number];
  mount: [number, number];
  pointer: [number, number];
};

type ParallaxOptions = {
  output: [number, number];
  axis?: Axis;
  disableOnMobile?: boolean;
  input?: [number, number];
};

export const PARALLAX_MEDIUM = {
  hero: {
    backgroundY: [-36, 36] as [number, number],
    atmosphereY: [26, -26] as [number, number],
    foregroundY: [-118, 118] as [number, number],
  },
  contact: {
    backgroundY: [-32, 32] as [number, number],
    headingY: [-90, 90] as [number, number],
  },
  section: {
    riseY: [92, -40] as [number, number],
    fade: [0.65, 1] as [number, number],
  },
  spotify: {
    riseY: [58, -24] as [number, number],
    fade: [0.72, 1] as [number, number],
  },
};

export const SCROLL_TIMELINE = {
  heroZoom: [0, 0.24] as [number, number],
  titleReveal: [0, 0.06, 0.18, 0.32] as [number, number, number, number],
  ctaReveal: [0.04, 0.11, 0.28, 0.34] as [number, number, number, number],
  socialReveal: [0.39, 0.46, 0.62, 0.68] as [number, number, number, number],
  spotifyReveal: [0.72, 0.78, 0.84, 0.89] as [number, number, number, number],
  contactReveal: [0.9, 0.93, 0.955, 0.965] as [number, number, number, number],
  finalTitleReveal: [0.975, 0.995, 1] as [number, number, number],
};

export const DESKTOP_SCROLL_TIMELINE = {
  heroZoom: SCROLL_TIMELINE.heroZoom,
  titleReveal: SCROLL_TIMELINE.titleReveal,
  compactCtaReveal: [0, 0.28, 0.34] as ThreePointRange,
  finalTitleReveal: SCROLL_TIMELINE.finalTitleReveal,
  stages: {
    cta: {
      reveal: SCROLL_TIMELINE.ctaReveal,
      active: [SCROLL_TIMELINE.ctaReveal[0], SCROLL_TIMELINE.ctaReveal[3]],
      mount: [0.025, 0.355] as [number, number],
      pointer: [SCROLL_TIMELINE.ctaReveal[0], SCROLL_TIMELINE.ctaReveal[3]],
    },
    social: {
      reveal: SCROLL_TIMELINE.socialReveal,
      active: [SCROLL_TIMELINE.socialReveal[0], SCROLL_TIMELINE.socialReveal[3]],
      mount: [0.37, 0.7] as [number, number],
      pointer: [SCROLL_TIMELINE.socialReveal[0], SCROLL_TIMELINE.socialReveal[3]],
    },
    spotify: {
      reveal: SCROLL_TIMELINE.spotifyReveal,
      active: [0.72, 0.89] as [number, number],
      mount: [0.7, 0.91] as [number, number],
      pointer: [0.72, 0.89] as [number, number],
    },
    contact: {
      reveal: SCROLL_TIMELINE.contactReveal,
      active: [0.9, 0.965] as [number, number],
      mount: [0.885, 0.975] as [number, number],
      pointer: [0.9, 0.965] as [number, number],
    },
  } satisfies Record<TimelineStage, StageWindow>,
};

export const MOBILE_SCROLL_TIMELINE = {
  heroZoom: SCROLL_TIMELINE.heroZoom,
  titleReveal: SCROLL_TIMELINE.titleReveal,
  compactCtaReveal: [0, 0.12, 0.145] as ThreePointRange,
  finalTitleReveal: [0.86, 0.94, 1] as ThreePointRange,
  stages: {
    cta: {
      reveal: [0, 0.12, 0.12, 0.145] as RevealRange,
      active: [0, 0.145] as [number, number],
      mount: [0, 0.16] as [number, number],
      pointer: [0, 0.145] as [number, number],
    },
    social: {
      reveal: [0.15, 0.175, 0.38, 0.415] as RevealRange,
      active: [0.15, 0.415] as [number, number],
      mount: [0.135, 0.43] as [number, number],
      pointer: [0.15, 0.415] as [number, number],
    },
    spotify: {
      reveal: [0.42, 0.445, 0.56, 0.585] as RevealRange,
      active: [0.42, 0.585] as [number, number],
      mount: [0.405, 0.6] as [number, number],
      pointer: [0.42, 0.585] as [number, number],
    },
    contact: {
      reveal: [0.59, 0.62, 0.79, 0.815] as RevealRange,
      active: [0.59, 0.815] as [number, number],
      mount: [0.575, 0.835] as [number, number],
      pointer: [0.59, 0.815] as [number, number],
    },
  } satisfies Record<TimelineStage, StageWindow>,
};

export function useParallaxStyle({
  output,
  axis = "y",
  disableOnMobile = true,
  input = [0, 1200],
}: ParallaxOptions): MotionStyle {
  const shouldReduceMotion = useReducedMotion();
  const isMobile = useMediaQuery(MEDIA_MOBILE);
  const { scrollY } = useScroll();

  const adjustedOutput: [number, number] = isMobile ? [output[0] * 0.25, output[1] * 0.25] : output;
  const transform = useTransform(scrollY, input, adjustedOutput);
  const enabled = !shouldReduceMotion && !(disableOnMobile && isMobile);

  if (axis === "x") {
    return { x: enabled ? transform : 0 };
  }

  return { y: enabled ? transform : 0 };
}

export function useParallaxOpacity(
  output: [number, number],
  disableOnMobile = true,
  input: [number, number] = [0, 900],
): MotionStyle {
  const shouldReduceMotion = useReducedMotion();
  const isMobile = useMediaQuery(MEDIA_MOBILE);
  const { scrollY } = useScroll();

  const opacity = useTransform(scrollY, input, output);
  const enabled = !shouldReduceMotion && !(disableOnMobile && isMobile);

  return { opacity: enabled ? opacity : 1 };
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();

    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);

  return matches;
}
